import React, { useCallback, useEffect, useState } from 'react';
import { useField } from 'react-final-form';

import { useAzureUpload } from 'shared/hooks/useAzureUpload/useAzureUpload';
import { createFetchUploadMeta } from 'api/actions/participant/participant.actions';
import { useParticipantQueryData } from 'contexts/participantQueryData/participantQueryDataContext';
import { AudioRecorder } from './AudioRecorder';
import { AudioRecorderContainerProps } from './AudioRecoder.types';
import { useVoiceRecorder } from 'shared/hooks';

export const AudioRecorderContainer = ({
  setIsAudioUploading,
  setRecordingStatus,
  onAudioReady,
  secondaryAction,
  registerDeleteHandler,
}: AudioRecorderContainerProps) => {
  const [blob, setBlob] = useState<Blob | undefined>();
  const { participantId } = useParticipantQueryData();
  const { upload, fileURI, loading } = useAzureUpload({
    fetchUploadMetaAction: createFetchUploadMeta(participantId),
  });

  const { mediaBlobUrl, status, startRecording, stopRecording, clearBlobUrl } = useVoiceRecorder({
    onAudioRecorded: setBlob,
    onStatusChange: setRecordingStatus,
  });

  const {
    input: { onChange: pronunciationAudioChange, value: pronunciationAudio },
  } = useField<string>('pronunciationAudio', {
    subscription: { value: true, valid: true },
  });

  const {
    input: { onChange: pronunciationAudioURIChange, value: pronunciationAudioURI },
  } = useField<string>('pronunciationAudioURI');

  useEffect(() => {
    pronunciationAudioURIChange(fileURI);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileURI]);

  useEffect(() => {
    if (onAudioReady) {
      onAudioReady(blob);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob]);

  useEffect(() => {
    if (blob) {
      upload(new File([blob], `${participantId}.wav`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob]);

  useEffect(() => {
    setIsAudioUploading(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleDelete = useCallback(() => {
    pronunciationAudioURIChange(null);
    pronunciationAudioChange(null);
    setBlob(undefined);
    onAudioReady?.(undefined);
    clearBlobUrl();
  }, [clearBlobUrl, onAudioReady, pronunciationAudioChange, pronunciationAudioURIChange]);

  useEffect(() => {
    registerDeleteHandler?.(handleDelete);
  }, [handleDelete, registerDeleteHandler]);

  return (
    <>
      <AudioRecorder
        onDelete={handleDelete}
        pronunciationAudioURI={pronunciationAudioURI}
        pronunciationAudio={pronunciationAudio}
        secondaryAction={secondaryAction}
        mediaBlobUrl={mediaBlobUrl}
        status={status}
        startRecording={startRecording}
        stopRecording={stopRecording}
      />
    </>
  );
};
